import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

/**
 * The rule every ownership guard in the Backoffice enforces, in one
 * sentence. A user is owned by exactly one customer (acc.Users.CustomerId:
 * whose licenses and seats apply to them) and is normally a member of that
 * customer's teams only. A membership that crosses customers is for one
 * company sharing Placepoint content with another company's user; it is
 * rare on purpose, and in practice almost always an operator picking the
 * wrong row. The guards never block it, they make the operator confirm it.
 */
export const CUSTOMER_OWNERSHIP_RULE =
  "A user is owned by one customer and normally only sits in that customer's teams. Crossing customers is for one company sharing content with another company's user, and is rare on purpose."

type OwnedUser = Pick<UserFragment, "Customer">
type TeamCustomerLink = Pick<
  TenantFragment,
  "ExternalCustomerId" | "CustomerName"
>

export type CrossCustomerMembership = {
  ownerName: string
  teamCustomerName: string
}

/**
 * The user's owner and the team's customer when putting (or keeping) the
 * user in the team crosses customers; null when it does not, including
 * when either side has no customer at all (nothing to contradict: a user
 * owned by nobody takes the team's customer when added).
 */
export function getCrossCustomerMembership(
  user: OwnedUser | null | undefined,
  team: TeamCustomerLink | null | undefined,
): CrossCustomerMembership | null {
  const ownerId = user?.Customer?.ExternalCustomerId
  const teamCustomerId = team?.ExternalCustomerId
  if (!ownerId || !teamCustomerId || ownerId === teamCustomerId) return null
  return {
    ownerName: user?.Customer?.Name ?? "another customer",
    teamCustomerName: team?.CustomerName ?? "another customer",
  }
}

export type CrossCustomerCount = {
  /** Users whose owner differs from the team's customer. */
  count: number
  total: number
  /** Distinct owner names among the crossing users, for the message. */
  ownerNames: string[]
}

/** The same test over many users against one team (bulk add to a team). */
export function countCrossCustomerMemberships(
  users: OwnedUser[],
  team: TeamCustomerLink | null | undefined,
): CrossCustomerCount {
  const ownerNames = new Set<string>()
  let count = 0
  for (const user of users) {
    const crossing = getCrossCustomerMembership(user, team)
    if (!crossing) continue
    count += 1
    ownerNames.add(crossing.ownerName)
  }
  return { count, total: users.length, ownerNames: [...ownerNames] }
}

/**
 * Whether moving the user to `customer` puts them with a customer none of
 * their teams belong to. A user without teams has nothing to contradict.
 * Read off the customer's team list, so no extra query is needed to judge
 * a candidate in the picker.
 */
export function isOwnerOutsideUserTeams(
  user: Pick<UserFragment, "Tenants">,
  customer: Pick<CustomerFragment, "Tenants">,
) {
  if (user.Tenants.length === 0) return false
  const customerTeamIds = new Set(customer.Tenants.map((tenant) => tenant.Id))
  return !user.Tenants.some((tenant) => customerTeamIds.has(tenant.Id))
}

/** The same test over many users against one candidate owner (bulk move). */
export function countOwnersOutsideUserTeams(
  users: Pick<UserFragment, "Tenants">[],
  customer: Pick<CustomerFragment, "Tenants">,
) {
  return users.filter((user) => isOwnerOutsideUserTeams(user, customer)).length
}

/**
 * For linking a team to `customer`: how many of the team's members are
 * owned by somebody else, and by whom. Members owned by nobody are not
 * counted (the link is what gives them an owner).
 */
export function countMembersOwnedElsewhere(
  members: OwnedUser[],
  customer: Pick<CustomerFragment, "ExternalCustomerId">,
): CrossCustomerCount {
  const ownerNames = new Set<string>()
  let count = 0
  for (const member of members) {
    const ownerId = member.Customer?.ExternalCustomerId
    if (!ownerId || ownerId === customer.ExternalCustomerId) continue
    count += 1
    ownerNames.add(member.Customer?.Name ?? "another customer")
  }
  return { count, total: members.length, ownerNames: [...ownerNames] }
}

/** "3 of 7 users" / "1 of 1 user", for the guard messages. */
export function formatCountOfTotal(count: number, total: number, noun: string) {
  return `${count} of ${total} ${noun}${total === 1 ? "" : "s"}`
}

/** "Fagerstad Utvikling AS", "A and B", "A, B and 2 others". */
export function formatOwnerNames(names: string[]) {
  if (names.length <= 2) return names.join(" and ")
  return `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length - 2 === 1 ? "" : "s"}`
}
